import React, {useState} from 'react';
import {injectIntl} from 'react-intl';
import {withRouter} from 'react-router-dom';
import {createRefetchContainer, graphql} from 'react-relay';
import {BaseLevelWrapper} from './BaseLevelWrapper';
import {GroupRow} from '../rows/GroupRow';
import {GLOBAL_FILTER_FIELD, GLOBAL_FILTER_OPERATOR, GroupProgressCategories} from '../../../../constants';

const SprintRelayWrapper = ({
	relay,
	tableHeader,
	groupings,
	searchQuery,
	viewer,
	intl,
	rowIndex,
	enabledColumns,
	nextLevelProps,
	sortValue,
	convertToProjectCurrency,
	projectCurrency,
}) => {
	const sprints = viewer.company.allSprintsGlobalSearch.edges;
	const company = viewer.company;
	const currency = convertToProjectCurrency && projectCurrency ? projectCurrency : viewer.company.currency;
	const hasNextPage = viewer.company.allSprintsGlobalSearch.pageInfo.hasNextPage;
	const [modalTasksFetching, setModalTasksFetching] = useState(false);

	const sprintGroupingFilter = sprintId => {
		return {
			field: GLOBAL_FILTER_FIELD.PROJECT_SPRINT,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: [sprintId],
		};
	};

	const noSprintGroupingFilter = () => {
		return {
			field: GLOBAL_FILTER_FIELD.SPRINT_CATEGORY,
			operator: GLOBAL_FILTER_OPERATOR.IS,
			value: GroupProgressCategories.NONE,
		};
	};

	const dataRows = sprints.map(sprint => {
		return {
			...sprint.node,
		};
	});

	if (company.taskTotals?.numberOfTasks) {
		dataRows.push({
			isNoSprint: true,
			name: intl.formatMessage({id: 'common.no_sprints'}),
			taskTotals: company.taskTotals,
		});
	}

	const data = {
		rows: dataRows,
		currency,
		groupingFilter: rowData => (rowData.isNoSprint ? noSprintGroupingFilter() : sprintGroupingFilter(rowData.id)),
	};

	const fetchMore = () => {
		if (!modalTasksFetching) {
			const refetchObject = {};
			setModalTasksFetching(true);
			refetchObject.pageSize = sprints.length + 30;
			relay.refetch(refetchObject, null, () => setModalTasksFetching(false));
		}
	};

	const sprintRows = () => {
		return GroupRow(
			data,
			intl,
			rowIndex,
			searchQuery,
			groupings,
			enabledColumns,
			sortValue,
			hasNextPage,
			fetchMore,
			nextLevelProps,
			tableHeader,
			convertToProjectCurrency,
			projectCurrency
		);
	};

	return rowIndex === 1 ? (
		<BaseLevelWrapper
			tableHeader={tableHeader}
			intl={intl}
			enabledColumns={enabledColumns}
			getRows={sprintRows}
			currentLength={sprints.length}
			hasNextPage={viewer.company.allSprintsGlobalSearch.pageInfo.hasNextPage}
			refetch={relay.refetch}
		/>
	) : (
		sprintRows()
	);
};

const SprintRelayWrapperQuery = graphql`
	query SprintRelayWrapperQuery_Query($searchQuery: TaskSearchQueryType, $pageSize: Int, $convertToProjectCurrency: Boolean) {
		viewer {
			actualPersonId
			component(name: "sprint_relay_wrapper")
			...SprintRelayWrapper_viewer
				@arguments(searchQuery: $searchQuery, pageSize: $pageSize, convertToProjectCurrency: $convertToProjectCurrency)
		}
	}
`;
export {SprintRelayWrapperQuery};

export default injectIntl(
	withRouter(
		createRefetchContainer(
			SprintRelayWrapper,
			{
				viewer: graphql`
					fragment SprintRelayWrapper_viewer on Viewer
					@argumentDefinitions(
						searchQuery: {type: "TaskSearchQueryType"}
						pageSize: {type: "Int"}
						convertToProjectCurrency: {type: "Boolean"}
					) {
						company {
							currency
							taskTotals(
								searchQuery: $searchQuery
								noSprint: true
								convertToProjectCurrency: $convertToProjectCurrency
							) {
								registeredMinutes
								billableActualMinutes
								nonBillableActualMinutes
								estimatePoints
								estimateMinutes
								remainingPoints
								remainingMinutes
								plannedRevenue
								actualRevenue
								remainingRevenue
								forecastRevenue
								recognitionForecastRevenue
								totalActualRevenueRecognition
								totalForecastRevenueToComplete
								surplus
								numberOfTasks
							}
							allSprintsGlobalSearch(first: $pageSize, searchQuery: $searchQuery) {
								pageInfo {
									endCursor
									hasNextPage
								}
								edges {
									node {
										id
										name
										taskTotals(searchQuery: $searchQuery) {
											registeredMinutes
											billableActualMinutes
											nonBillableActualMinutes
											estimatePoints
											estimateMinutes
											remainingPoints
											remainingMinutes
											plannedRevenue
											actualRevenue
											remainingRevenue
											forecastRevenue
											recognitionForecastRevenue
											totalActualRevenueRecognition
											totalForecastRevenueToComplete
											surplus
											numberOfTasks
										}
									}
								}
							}
						}
					}
				`,
			},
			graphql`
				query SprintRelayWrapperRefetchQuery(
					$pageSize: Int
					$searchQuery: TaskSearchQueryType
					$convertToProjectCurrency: Boolean
				) {
					viewer {
						...SprintRelayWrapper_viewer
							@arguments(
								pageSize: $pageSize
								searchQuery: $searchQuery
								convertToProjectCurrency: $convertToProjectCurrency
							)
					}
				}
			`
		)
	)
);
